import { z } from 'zod';

import { CashLocationStatusSchema, CashLocationTypeSchema } from './cash-locations.schema.js';
import {
  CashMovementDirectionSchema,
  CashMovementDtoSchema,
  CashMovementSourceTypeSchema,
} from './cash-movement.schema.js';
import { CashSessionDtoSchema } from './cash-sessions.schema.js';
import {
  CashVarianceActionDtoSchema,
  CashVarianceActionTypeSchema,
  CashVarianceCaseDtoSchema,
} from './cash-variance.schema.js';
import {
  cashAdminIncidentStatusValues,
  cashAdminIncidentTypeValues,
  cashAuditTimelineCategoryValues,
  cashAuditTimelineEventTypeValues,
  type CashAdminIncidentStatus,
  type CashAdminIncidentSummaryDto,
  type CashAdminIncidentType,
  type CashAuditActorRefDto,
  type CashAuditCashLocationRefDto,
  type CashAuditPurchasePayoutLinkDto,
  type CashAuditTimelineCategory,
  type CashAuditTimelineEventDto,
  type CashAuditTimelineEventType,
  type GetCashAdminIncidentAuditRequest,
  type GetCashAdminIncidentAuditResponse,
  type ListCashAdminIncidentsRequest,
  type ListCashAdminIncidentsResponse,
} from './cash-audit.dto.js';

const timestampSchema = z.iso.datetime();
const technicalIdsSchema = z.record(z.string().trim().min(1), z.string().trim().min(1));

export const CashAdminIncidentTypeSchema: z.ZodType<CashAdminIncidentType> = z.enum(
  cashAdminIncidentTypeValues,
);
export const CashAdminIncidentStatusSchema: z.ZodType<CashAdminIncidentStatus> = z.enum(
  cashAdminIncidentStatusValues,
);
export const CashAuditTimelineCategorySchema: z.ZodType<CashAuditTimelineCategory> = z.enum(
  cashAuditTimelineCategoryValues,
);
export const CashAuditTimelineEventTypeSchema: z.ZodType<CashAuditTimelineEventType> = z.enum(
  cashAuditTimelineEventTypeValues,
);

export const CashAuditActorRefDtoSchema: z.ZodType<CashAuditActorRefDto> = z.object({
  userId: z.uuid(),
  displayName: z.string().trim().min(1).max(160).nullable(),
  email: z.email().nullable(),
  employeeId: z.uuid().nullable(),
  employeeNumber: z.string().trim().min(1).max(80).nullable(),
});

export const CashAuditCashLocationRefDtoSchema: z.ZodType<CashAuditCashLocationRefDto> =
  z.object({
    cashLocationId: z.uuid(),
    name: z.string().trim().min(1).max(120),
    type: CashLocationTypeSchema.nullable(),
    status: CashLocationStatusSchema.nullable(),
    storeId: z.uuid().nullable(),
    storeName: z.string().trim().min(1).max(160).nullable(),
    locationId: z.uuid().nullable(),
    locationName: z.string().trim().min(1).max(160).nullable(),
    stationId: z.uuid().nullable(),
    stationName: z.string().trim().min(1).max(160).nullable(),
  });

export const CashAuditPurchasePayoutLinkDtoSchema: z.ZodType<CashAuditPurchasePayoutLinkDto> =
  z.object({
    purchaseId: z.uuid(),
    folioNumber: z.number().int().positive().nullable(),
    movementId: z.uuid(),
    committedAt: timestampSchema.nullable(),
    cashSessionId: z.uuid().nullable(),
    cashLocationId: z.uuid().nullable(),
    operator: CashAuditActorRefDtoSchema.nullable(),
    paidAmountMinor: z.number().int().nonnegative(),
    cancellationState: z.enum(['active', 'canceled', 'unknown']),
    canceledAt: timestampSchema.nullable(),
    technicalIds: technicalIdsSchema,
  });

export const CashAuditTimelineEventDtoSchema: z.ZodType<CashAuditTimelineEventDto> = z.object({
  id: z.string().trim().min(1).max(240),
  timestamp: timestampSchema,
  category: CashAuditTimelineCategorySchema,
  eventType: CashAuditTimelineEventTypeSchema,
  title: z.string().trim().min(1).max(160),
  description: z.string().trim().min(1).max(500).nullable(),
  amountMinor: z.number().int().nullable(),
  direction: CashMovementDirectionSchema.nullable(),
  actor: CashAuditActorRefDtoSchema.nullable(),
  cashLocationId: z.uuid().nullable(),
  cashSessionId: z.uuid().nullable(),
  caseId: z.uuid().nullable(),
  actionId: z.uuid().nullable(),
  movementId: z.uuid().nullable(),
  purchaseId: z.uuid().nullable(),
  sourceType: CashMovementSourceTypeSchema.nullable(),
  sourceId: z.string().trim().min(1).max(200).nullable(),
  fundingSourceCategoryId: z.uuid().nullable().optional(),
  fundingSourceCategoryCodeSnapshot: z.string().trim().min(1).max(80).nullable().optional(),
  fundingSourceCategoryDisplayNameSnapshot: z.string().trim().min(1).max(120).nullable().optional(),
  technicalIds: technicalIdsSchema,
});

export const CashAdminIncidentSummaryDtoSchema: z.ZodType<CashAdminIncidentSummaryDto> =
  z.object({
    id: z.string().trim().min(1).max(240),
    companyId: z.uuid(),
    caseId: z.uuid().nullable(),
    cashSessionId: z.uuid(),
    cashLocation: CashAuditCashLocationRefDtoSchema,
    incidentType: CashAdminIncidentTypeSchema,
    status: CashAdminIncidentStatusSchema,
    initialAmountMinor: z.number().int().nullable(),
    outstandingAmountMinor: z.number().int().nullable(),
    lastActionType: CashVarianceActionTypeSchema.nullable(),
    openedAt: timestampSchema,
    lastEventAt: timestampSchema,
    openedBy: CashAuditActorRefDtoSchema.nullable(),
    lastActor: CashAuditActorRefDtoSchema.nullable(),
    technicalIds: technicalIdsSchema,
  });

export const ListCashAdminIncidentsRequestSchema: z.ZodType<ListCashAdminIncidentsRequest> =
  z.object({
    companyId: z.uuid(),
    storeId: z.uuid().optional(),
    locationId: z.uuid().optional(),
    stationId: z.uuid().optional(),
    cashLocationId: z.uuid().optional(),
    incidentType: CashAdminIncidentTypeSchema.optional(),
    status: CashAdminIncidentStatusSchema.optional(),
    actorUserId: z.uuid().optional(),
    from: timestampSchema.optional(),
    to: timestampSchema.optional(),
    unresolvedOnly: z.boolean().optional(),
  });

export const ListCashAdminIncidentsResponseSchema: z.ZodType<ListCashAdminIncidentsResponse> =
  z.object({
    items: z.array(CashAdminIncidentSummaryDtoSchema),
    actorRefs: z.array(CashAuditActorRefDtoSchema),
  });

export const GetCashAdminIncidentAuditRequestSchema: z.ZodType<GetCashAdminIncidentAuditRequest> =
  z
    .object({
      companyId: z.uuid(),
      caseId: z.uuid().optional(),
      cashSessionId: z.uuid().optional(),
    })
    .superRefine((value, context) => {
      const selectors = [value.caseId, value.cashSessionId].filter(Boolean);
      if (selectors.length !== 1) {
        context.addIssue({
          code: 'custom',
          message: 'Exactly one of caseId or cashSessionId is required.',
          path: ['caseId'],
        });
      }
    });

export const GetCashAdminIncidentAuditResponseSchema: z.ZodType<GetCashAdminIncidentAuditResponse> =
  z.object({
    incident: CashAdminIncidentSummaryDtoSchema,
    session: CashSessionDtoSchema,
    cashLocation: CashAuditCashLocationRefDtoSchema,
    varianceCase: CashVarianceCaseDtoSchema.nullable(),
    actions: z.array(CashVarianceActionDtoSchema),
    movements: z.array(CashMovementDtoSchema),
    purchasePayoutLinks: z.array(CashAuditPurchasePayoutLinkDtoSchema),
    actorRefs: z.array(CashAuditActorRefDtoSchema),
    timeline: z.array(CashAuditTimelineEventDtoSchema),
    technicalIds: technicalIdsSchema,
  });
